import React from "react";
import AdminNavbar from "./AdminNavbar";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grow,
    IconButton,
    InputAdornment,
    InputLabel,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SecurityIcon from "@mui/icons-material/Security";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SupervisedUserCircleIcon from "@mui/icons-material/SupervisedUserCircle";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import GroupIcon from "@mui/icons-material/Group";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AdminData from "../../backend/dataservices/AdminData";
import AdminPermissionsDialog from "./AdminPermissionsDialog";
import AdminUserRelationsDialog from "./AdminUserRelationsDialog";
import AdminCreateUserDialog from "./AdminCreateUserDialog";
import User from "../../backend/User";
import {isTenantAdminFromUsers} from "../../utils/TenantAdminAccess";

function AdminUsersPage() {

    const [users, setUsers] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [roles, setRoles] = React.useState([]);
    const [tenants, setTenants] = React.useState([]);
    const [editUser, setEditUser] = React.useState(null);
    const [selectedRole, setSelectedRole] = React.useState('');
    const [editGid, setEditGid] = React.useState('');
    const [editTenantId, setEditTenantId] = React.useState('');
    const [availableDiszis, setAvailableDiszis] = React.useState([]);
    const [selectedDiszi, setSelectedDiszi] = React.useState('');
    const [initialDiszi, setInitialDiszi] = React.useState('');
    const [hasPermissions, setHasPermissions] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [snackbarOpen, setSnackbarOpen] = React.useState(false);
    const [snackbarText, setSnackbarText] = React.useState("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState("success");
    const [permUser, setPermUser] = React.useState(null);
    const [relationUser, setRelationUser] = React.useState(null);
    const [createOpen, setCreateOpen] = React.useState(false);

    const [search, setSearch] = React.useState('');
    const [showHidden, setShowHidden] = React.useState(false);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(25);

    const loadUsers = React.useCallback(() => {
        return AdminData.getUsers().then(data => {
            setUsers(Array.isArray(data) ? data : []);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    React.useEffect(() => {
        loadUsers();
        AdminData.getRoles().then(data => {
            if (Array.isArray(data)) setRoles(data);
        }).catch(() => {});
        AdminData.getTenants().then(data => {
            if (Array.isArray(data)) setTenants(data);
        }).catch(() => {});
    }, [loadUsers]);

    const filteredUsers = React.useMemo(() => {
        let result = users;
        if (!showHidden) {
            result = result.filter(u => !u.U_TenantHidden || !u.U_TenantID || u.U_IsAdmin);
        }
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            result = result.filter(u =>
                (u.U_Benutzername || '').toLowerCase().includes(q) ||
                (u.U_FullName || '').toLowerCase().includes(q) ||
                (u.U_Email || '').toLowerCase().includes(q) ||
                (u.U_TenantCode || '').toLowerCase().includes(q)
            );
        }
        return result;
    }, [users, search, showHidden]);

    React.useEffect(() => { setPage(0); }, [search, showHidden]);

    const pagedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const isLocalUser = editUser?.U_AuthProvider === 'local';
    const isEntraUser = editUser?.U_AuthProvider === 'entraid';
    const isTenantAdmin = React.useMemo(() => isTenantAdminFromUsers(users), [users]);

    // Ein User mit einer Rolle im Scope "single" IST eine Fuehrungskraft und
    // sieht genau deren Mitarbeiter. Welches Kuerzel das ist, ist im Datenmodell
    // nicht hinterlegt und muss vom Admin gewaehlt werden.
    const needsDiszi = React.useMemo(
        () => roles.find(r => r.name === selectedRole)?.disziScope === 'single',
        [roles, selectedRole]
    );

    function handleEditClick(user) {
        setEditUser(user);
        setSelectedRole(user.U_Rolle || '');
        setEditGid(user.U_GID || '');
        setEditTenantId(user.U_TenantID ?? '');
        setSelectedDiszi('');
        setInitialDiszi('');
        setAvailableDiszis([]);
        setHasPermissions(true);

        if (!user.U_TenantID) return;
        AdminData.getDiszis(user.U_TenantID)
            .then(data => setAvailableDiszis(Array.isArray(data) ? data : []))
            .catch(() => {});
        // Bereits vergebene Fuehrungskraft vorbelegen, damit ein Speichern sie
        // nicht unbemerkt entfernt.
        AdminData.getUserPermissions(user.U_ID, user.U_TenantID).then(data => {
            const perms = data?.permissions || [];
            const assigned = [...new Set(perms
                .filter(p => p.R_Tabelle === 'TAB_MITARBEITER' && p.R_Diszi)
                .map(p => p.R_Diszi))];
            setSelectedDiszi(assigned[0] || '');
            setInitialDiszi(assigned[0] || '');
            setHasPermissions(perms.length > 0);
        }).catch(() => {});
    }

    function handleDialogClose() {
        setEditUser(null);
        setSelectedRole('');
        setEditGid('');
        setEditTenantId('');
        setSelectedDiszi('');
        setInitialDiszi('');
        setAvailableDiszis([]);
    }

    function handleImpersonate(user) {
        AdminData.impersonateUser(user.U_ID, user.U_TenantID).then(data => {
            User.startImpersonation(data.token);
            window.location = process.env.PUBLIC_URL + (User.getHomepage() || '/start');
        }).catch(() => {
            setSnackbarSeverity('error');
            setSnackbarText('Impersonierung fehlgeschlagen.');
            setSnackbarOpen(true);
        });
    }

    function handleToggleAdmin(user) {
        const newFlag = !user.U_IsAdmin;
        AdminData.setAdminFlag(user.U_ID, newFlag).then(() => {
            setUsers(prev => prev.map(u =>
                u.U_ID === user.U_ID && u.U_TenantID === user.U_TenantID
                    ? { ...u, U_IsAdmin: newFlag ? 1 : 0 }
                    : u
            ));
            setSnackbarSeverity('success');
            setSnackbarText(newFlag ? 'Admin-Rechte erteilt.' : 'Admin-Rechte entzogen.');
            setSnackbarOpen(true);
        }).catch(err => {
            setSnackbarSeverity('error');
            setSnackbarText(err?.response?.data?.error || 'Fehler beim Ändern der Admin-Rechte.');
            setSnackbarOpen(true);
        });
    }

    function handleSaveUser() {
        if (!editUser) return;
        setSaving(true);

        const data = { role: selectedRole || undefined };
        if (needsDiszi) {
            data.diszis = selectedDiszi ? [selectedDiszi] : [];
        }
        if (isLocalUser) {
            data.gid = editGid.trim() || null;
        }
        if (isTenantAdmin) {
            data.newTenantId = editTenantId !== '' ? (editTenantId || null) : null;
        }

        AdminData.updateUser(editUser.U_ID, editUser.U_TenantID, data).then(() => {
            const newTenantId = isTenantAdmin ? (editTenantId || null) : editUser.U_TenantID;
            const tenant = tenants.find(t => t.T_ID === newTenantId);
            setUsers(prev => prev.map(u => {
                if (u.U_ID !== editUser.U_ID || u.U_TenantID !== editUser.U_TenantID) return u;
                return {
                    ...u,
                    U_Rolle: selectedRole || u.U_Rolle,
                    ...(isTenantAdmin ? {
                        U_TenantID: newTenantId,
                        U_TenantCode: tenant?.T_Code ?? null,
                        U_TenantHidden: tenant?.T_Hidden ?? 0,
                    } : {}),
                    ...(isLocalUser ? { U_GID: editGid.trim() || null } : {})
                };
            }));
            setSnackbarSeverity('success');
            setSnackbarText('Benutzer gespeichert.');
            setSnackbarOpen(true);
            handleDialogClose();
        }).catch(err => {
            setSnackbarSeverity('error');
            setSnackbarText(err?.response?.data?.error || 'Fehler beim Speichern.');
            setSnackbarOpen(true);
        }).finally(() => setSaving(false));
    }

    const diszisChanged = needsDiszi && selectedDiszi !== initialDiszi;

    const hasChanges = editUser && (
        selectedRole !== (editUser.U_Rolle || '') ||
        diszisChanged ||
        // Konten aus der Migration tragen eine Rolle, aber keine Rechte. Ohne
        // diesen Fall liesse sich die vorhandene Rolle nicht erneut zuweisen,
        // um die fehlenden Rechte zu setzen.
        (!!selectedRole && !hasPermissions) ||
        (isLocalUser && editGid !== (editUser.U_GID || '')) ||
        (isTenantAdmin && String(editTenantId) !== String(editUser.U_TenantID ?? ''))
    );

    return (
        <div>
            <AdminNavbar initValue={"/admin/users"}/>
            <Box sx={{padding: '20px'}}>
                <Typography variant={"h6"} sx={{marginBottom: '16px'}}>Benutzerverwaltung</Typography>

                <Box sx={{display: 'flex', alignItems: 'center', gap: 2, marginBottom: '12px', flexWrap: 'wrap'}}>
                    <TextField
                        size="small"
                        placeholder="Suchen nach Name, E-Mail, Tenant …"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        sx={{width: 340}}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{color: 'text.secondary'}}/>
                                </InputAdornment>
                            ),
                            endAdornment: search ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearch('')}>
                                        <ClearIcon fontSize="small"/>
                                    </IconButton>
                                </InputAdornment>
                            ) : null
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<PersonAddIcon/>}
                        onClick={() => setCreateOpen(true)}
                    >
                        Konto anlegen
                    </Button>
                    <FormControlLabel
                        control={<Switch checked={showHidden} onChange={e => setShowHidden(e.target.checked)} size="small"/>}
                        label={<Typography variant="body2">Versteckte Tenants anzeigen</Typography>}
                        sx={{ml: 'auto'}}
                    />
                </Box>

                {loading ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', marginTop: '40px'}}>
                        <CircularProgress/>
                    </Box>
                ) : (
                    <Paper elevation={3}>
                        <TableContainer>
                            <Table size={"small"}>
                                <TableHead>
                                    <TableRow sx={{'& th': {fontWeight: 'bold', backgroundColor: '#f5f5f5'}}}>
                                        <TableCell>Benutzername</TableCell>
                                        <TableCell>Vollständiger Name</TableCell>
                                        <TableCell>E-Mail</TableCell>
                                        <TableCell>Rolle</TableCell>
                                        <TableCell>Admin</TableCell>
                                        <TableCell>Auth-Provider</TableCell>
                                        <TableCell>GID</TableCell>
                                        <TableCell>OrgCode</TableCell>
                                        <TableCell>Tenant</TableCell>
                                        <TableCell>Relationen</TableCell>
                                        <TableCell align={"center"}>Aktion</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pagedUsers.map(user => (
                                        <TableRow
                                            key={`${user.U_TenantID}-${user.U_ID}`}
                                            hover
                                            sx={user.U_TenantHidden ? {opacity: 0.6} : {}}
                                        >
                                            <TableCell>{user.U_Benutzername}</TableCell>
                                            <TableCell>{user.U_FullName ?? '—'}</TableCell>
                                            <TableCell>{user.U_Email ?? '—'}</TableCell>
                                            <TableCell>{user.U_Rolle ?? '—'}</TableCell>
                                            <TableCell>{user.U_IsAdmin ? 'Ja' : '—'}</TableCell>
                                            <TableCell>{user.U_AuthProvider}</TableCell>
                                            <TableCell>{user.U_GID ?? '—'}</TableCell>
                                            <TableCell>{user.U_OrgCode ?? '—'}</TableCell>
                                            <TableCell>{user.U_TenantCode ?? '—'}</TableCell>
                                            <TableCell>
                                                {user.U_RelationCount > 0 ? (
                                                    <Tooltip title={"Muss nach dem Login eines dieser Konten übernehmen"}>
                                                        <Chip size="small" color="primary" variant="outlined"
                                                              label={`${user.U_RelationCount} Konten`}/>
                                                    </Tooltip>
                                                ) : user.U_RelationTargetCount > 0 ? (
                                                    <Tooltip title={"Dieses Konto ist Ziel einer User-Relation"}>
                                                        <Chip size="small" variant="outlined" label={"Ziel"}/>
                                                    </Tooltip>
                                                ) : '—'}
                                            </TableCell>
                                            <TableCell align={"center"}>
                                                <Tooltip title={"Benutzer bearbeiten"}>
                                                    <IconButton size={"small"} onClick={() => handleEditClick(user)}>
                                                        <EditIcon fontSize={"small"}/>
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={"Berechtigungen bearbeiten"}>
                                                    <IconButton size={"small"} onClick={() => setPermUser(user)}>
                                                        <SecurityIcon fontSize={"small"}/>
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={user.U_IsAdmin ? "Admin-Rechte entziehen" : "Zum Admin machen"}>
                                                    <IconButton size={"small"} onClick={() => handleToggleAdmin(user)}
                                                                color={user.U_IsAdmin ? "primary" : "default"}>
                                                        <AdminPanelSettingsIcon fontSize={"small"}/>
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={"User-Relationen verwalten"}>
                                                    <IconButton size={"small"} onClick={() => setRelationUser(user)}
                                                                color={user.U_RelationCount > 0 ? "primary" : "default"}>
                                                        <GroupIcon fontSize={"small"}/>
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={"Als dieser Benutzer anmelden"}>
                                                    <IconButton size={"small"} onClick={() => handleImpersonate(user)}>
                                                        <SupervisedUserCircleIcon fontSize={"small"}/>
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {pagedUsers.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={11} align={"center"} sx={{color: 'text.secondary'}}>
                                                {search ? 'Keine Benutzer gefunden.' : 'Keine Benutzer vorhanden.'}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={filteredUsers.length}
                            page={page}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            rowsPerPageOptions={[25, 50, 100]}
                            labelRowsPerPage="Zeilen pro Seite:"
                            labelDisplayedRows={({ from, to, count }) => `${from}–${to} von ${count}`}
                        />
                    </Paper>
                )}
            </Box>

            <Dialog open={editUser !== null} onClose={handleDialogClose} maxWidth="xs" fullWidth>
                <DialogTitle>Benutzer bearbeiten</DialogTitle>
                <DialogContent>
                    {editUser && (
                        <Box sx={{pt: 1, display: 'flex', flexDirection: 'column', gap: 2}}>
                            <Typography variant="body2" color="text.secondary">
                                <strong>{editUser.U_FullName || editUser.U_Benutzername}</strong>
                                {' · '}{editUser.U_AuthProvider}
                            </Typography>

                            <FormControl fullWidth size="small">
                                <InputLabel>Rolle</InputLabel>
                                <Select
                                    value={selectedRole}
                                    label="Rolle"
                                    onChange={e => setSelectedRole(e.target.value)}
                                >
                                    {roles.map(role => (
                                        <MenuItem key={role.name} value={role.name}>{role.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {needsDiszi && (
                                <FormControl fullWidth size="small">
                                    <InputLabel>Führungskraft</InputLabel>
                                    <Select
                                        value={selectedDiszi}
                                        label="Führungskraft"
                                        onChange={e => setSelectedDiszi(e.target.value)}
                                    >
                                        <MenuItem value=""><em>Keine</em></MenuItem>
                                        {availableDiszis.map(d => (
                                            <MenuItem key={d.D_Diszi} value={d.D_Diszi}>
                                                <ListItemText primary={d.D_Name || d.D_Diszi} secondary={d.D_Diszi}/>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>
                                        {selectedDiszi
                                            ? 'Der Benutzer sieht nur die Mitarbeiter dieser Führungskraft.'
                                            : 'Ohne Auswahl sieht der Benutzer keine Mitarbeiter.'}
                                    </FormHelperText>
                                </FormControl>
                            )}

                            {(isLocalUser || isTenantAdmin) && <Divider/>}
                            {isLocalUser && (
                                <TextField
                                    label="GID"
                                    size="small"
                                    fullWidth
                                    value={editGid}
                                    onChange={e => setEditGid(e.target.value)}
                                    placeholder="leer = keine GID"
                                />
                            )}
                            {isTenantAdmin && (
                                <FormControl fullWidth size="small">
                                    <InputLabel>Tenant</InputLabel>
                                    <Select
                                        value={editTenantId}
                                        label="Tenant"
                                        onChange={e => setEditTenantId(e.target.value)}
                                    >
                                        <MenuItem value=""><em>Kein Tenant</em></MenuItem>
                                        {tenants.map(t => (
                                            <MenuItem key={t.T_ID} value={t.T_ID}>
                                                {t.T_Code} – {t.T_Name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            {isTenantAdmin && isEntraUser && (
                                <Typography variant="caption" color="text.secondary">
                                    Bei EntraID-Benutzern wird der Tenant beim nächsten Login automatisch anhand des OrgCodes aktualisiert.
                                </Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} style={{color: 'gray'}}>Abbrechen</Button>
                    <Button
                        onClick={handleSaveUser}
                        color="primary"
                        disabled={!hasChanges || saving}
                    >
                        {saving ? <CircularProgress size={20}/> : 'Speichern'}
                    </Button>
                </DialogActions>
            </Dialog>

            {permUser && (
                <AdminPermissionsDialog
                    user={permUser}
                    onClose={() => setPermUser(null)}
                    onSaved={() => {
                        setSnackbarSeverity('success');
                        setSnackbarText('Berechtigungen gespeichert.');
                        setSnackbarOpen(true);
                    }}
                />
            )}

            {relationUser && (
                <AdminUserRelationsDialog
                    user={relationUser}
                    allUsers={users}
                    onClose={() => setRelationUser(null)}
                    onSaved={(count) => {
                        loadUsers();
                        setSnackbarSeverity('success');
                        setSnackbarText(count > 0
                            ? `${count} Konto/Konten zugewiesen. Der Benutzer muss nach dem Login eines davon übernehmen.`
                            : 'Relationen entfernt. Der Benutzer arbeitet wieder unter seinem eigenen Konto.');
                        setSnackbarOpen(true);
                    }}
                />
            )}

            {createOpen && (
                <AdminCreateUserDialog
                    roles={roles}
                    tenants={tenants}
                    onClose={() => setCreateOpen(false)}
                    onCreated={() => {
                        loadUsers();
                        setSnackbarSeverity('success');
                        setSnackbarText('Konto angelegt.');
                        setSnackbarOpen(true);
                    }}
                />
            )}

            <Snackbar open={snackbarOpen} autoHideDuration={5000} onClose={() => setSnackbarOpen(false)}
                      anchorOrigin={{vertical: "top", horizontal: "right"}} TransitionComponent={Grow}>
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{width: '100%'}}>
                    {snackbarText}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default AdminUsersPage;
